'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'
import { Button } from '@/components/button'
import { LegalMarkdown } from '@/components/legal-markdown'
import { T } from '@/components/t'
import { Language, Locale, parseLanguageFromLocale } from '@/setup/locales'
import { englishPrivacyPolicy } from './content/english-privacy-policy'
import { germanPrivacyPolicy } from './content/german-privacy-policy'

type PrivacyPolicyType = 'website' | 'app'

// The App privacy policy still lives on iubenda — only the Website policy is served inline.
const DEFAULT_APP_PRIVACY_POLICY_URL = 'https://www.iubenda.com/privacy-policy/34130103'

const APP_PRIVACY_POLICY_URLS: Partial<Record<Locale, string>> = {
    [Locale.DE_DE]: 'https://www.iubenda.com/privacy-policy/90645893',
    [Locale.DE_AT]: 'https://www.iubenda.com/privacy-policy/90645893',
    [Locale.NL_NL]: 'https://www.iubenda.com/privacy-policy/82697631',
}

export const PrivacyPolicyLandingPage = () => {
    const t = useTranslations()
    const locale = useLocale()

    const [selectedType, setSelectedType] = useState<PrivacyPolicyType>('website')

    const language = parseLanguageFromLocale(locale)
    const content = language === Language.DE ? germanPrivacyPolicy : englishPrivacyPolicy
    const appPrivacyPolicyUrl =
        APP_PRIVACY_POLICY_URLS[locale as Locale] ?? DEFAULT_APP_PRIVACY_POLICY_URL

    return (
        <div className="flex flex-col w-full items-center justify-center">
            <div className="flex gap-2 mt-8 justify-center">
                <Button
                    styleType={selectedType === 'website' ? 'primary' : 'secondary'}
                    onClick={() => setSelectedType('website')}
                >
                    {t('privacy-policy.website')}
                </Button>

                <Button
                    styleType={selectedType === 'app' ? 'primary' : 'secondary'}
                    onClick={() => setSelectedType('app')}
                >
                    {t('privacy-policy.app')}
                </Button>
            </div>

            {selectedType === 'website' ? (
                <LegalMarkdown content={content} />
            ) : (
                <div className="flex flex-col items-center gap-6 mt-16 mb-20">
                    <T
                        p
                        t="privacy-policy.app-cta-description"
                        className="text-center max-w-[600px] px-4"
                    />

                    <Button
                        shape="pill"
                        width="max-content"
                        height="46px"
                        styleType="primary"
                        href={{ externalUrl: appPrivacyPolicyUrl }}
                        target="_blank"
                    >
                        {t('privacy-policy.app-cta')}
                    </Button>
                </div>
            )}
        </div>
    )
}
